import React, { FC, ReactElement, useState, useEffect, forwardRef } from 'react';
import { useHistory } from 'react-router-dom';
import { SIGN_IN } from '@public/constants/routes';
import Markdown from 'react-markdown';
import { makeStyles, createStyles, useTheme, Theme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Slide from '@material-ui/core/Slide';
import { TransitionProps } from '@material-ui/core/transitions';

import config from '@core/constants/config';

const USER_AGREEMENT_ID = 'user-agreement';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    button: {
      minWidth: theme.spacing(30),
    },
    markdownContainer: {
      '& a': {
        color: theme.palette.primary.main,
      },
    },
    dialogContent: {
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(4),
      },
    },
    dialogTitle: {
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(4),
      },
    },
    dialogActions: {
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(4),
      },
    },
  }),
);

const DialogTransition = forwardRef((
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) => <Slide ref={ref} direction="up" {...props} />);

const UserAgreement: FC = () => {
  const classes = useStyles();
  const history = useHistory();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));

  const [open, setOpen] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [markdown, setMarkdown] = useState<string>('');

  const handleClose = (): void => {
    setOpen(false);
    history.push(SIGN_IN);
  };

  useEffect((): (() => void) => {
    const fileReader: FileReader = new FileReader();
    let unmounted = false;

    fileReader.onloadend = (): void => {
      if (!unmounted) {
        setMarkdown(fileReader.result as string);
        setLoaded(true);
        setOpen(true);
      }
    };

    fetch(`${process.env.PUBLIC_URL}/${config.personalLicense}`).then((response: Response) =>
      response.blob().then((value: Blob) => fileReader.readAsText(value)),
    );

    return (): void => {
      unmounted = true;
    };
  }, []);

  const dialogElementContent: ReactElement = (
    <>
      <DialogTitle className={classes.dialogTitle}>Лицензионное соглашение</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Markdown className={classes.markdownContainer} escapeHtml={false} source={markdown} />
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          className={classes.button}
          id={`${USER_AGREEMENT_ID}__close-button`}
          color="primary"
          onClick={handleClose}
        >
          На главную
        </Button>
      </DialogActions>
    </>
  );

  return (
    <>
      <Dialog
        TransitionComponent={isXs ? DialogTransition : undefined}
        onClose={handleClose}
        open={open && loaded}
        maxWidth="xl"
        fullScreen={isXs}
      >
        {dialogElementContent}
      </Dialog>
    </>
  );
};

export default UserAgreement;
